/**
 * GeosminPredictorPage — Wang 2010 geosmin & 2-MIB Taste-and-Odour predictor
 *
 * Geosmin (earthy) and 2-MIB (musty) are produced primarily by Anabaena and Oscillatoria
 * cyanobacteria. Wang et al. (2010) fitted a multivariate regression on 4 Chinese reservoirs
 * giving geosmin concentration as a function of chl-a, cyano fraction, T and stratification.
 *
 *   Geosmin (ng/L) = 4.2 × chl × f_cyano × (1 + 0.08 × (T − 15)) × (stratified ? 1.6 : 1.0)
 *
 * Reference: Wang, Z., Xu, Y., Shao, J., Wang, J. & Li, R. (2010) "Genes associated with
 *            2-methylisoborneol and geosmin production in cyanobacteria from drinking-water
 *            sources in China" Water Research 45(4): 1631-1640, Institute of Hydrobiology,
 *            Chinese Academy of Sciences, Wuhan.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { AlertOctagon, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

const DWI_GEOSMIN_NG_L = 10;  // UK DWI customer-acceptable threshold (≈ human sensory limit)
const DWI_MIB_NG_L = 10;

export default function GeosminPredictorPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('geosmin_predictor');
  const [chl, setChl] = useState(20);            // µg/L
  const [fCyano, setFCyano] = useState(0.45);    // fraction
  const [tempC, setTempC] = useState(18);
  const [stratified, setStrat] = useState(true);

  const R = useMemo(() => {
    const geosmin = 4.2 * chl * fCyano * (1 + 0.08 * (tempC - 15)) * (stratified ? 1.6 : 1.0);
    const mib = 2.8 * chl * fCyano * (1 + 0.06 * (tempC - 15)) * (stratified ? 1.4 : 1.0);
    const breachGeosmin = geosmin > DWI_GEOSMIN_NG_L;
    const breachMib = mib > DWI_MIB_NG_L;
    const breachFactor = Math.max(geosmin / DWI_GEOSMIN_NG_L, mib / DWI_MIB_NG_L);
    return { geosmin, mib, breachGeosmin, breachMib, breachFactor };
  }, [chl, fCyano, tempC, stratified]);

  const fmt = (n: number, d = 1) => isFinite(n) ? n.toFixed(d) : '—';

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={AlertOctagon} title="Module — Geosmin / 2-MIB Predictor"
        subtitle="Wang 2010: cyanobacterial Taste & Odour forecasting for water treatment risk"
        actions={<div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
            <input type="checkbox" checked={enabled} onChange={() => toggle('geosmin_predictor')} className="accent-emerald-500" /> Module {enabled ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={() => navigate('/modules')} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"><ArrowLeft className="w-4 h-4" /> Modules</button>
        </div>}
      />
      <ModuleConflictBanner moduleId="geosmin_predictor" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className={`rounded-xl border-2 p-4 ${R.breachGeosmin ? 'border-red-700 bg-red-900/20' : 'border-emerald-700 bg-emerald-900/15'}`}>
          <div className="text-xs uppercase tracking-wider text-slate-300">Predicted Geosmin</div>
          <div className={`text-2xl font-bold mt-1 ${R.breachGeosmin ? 'text-red-200' : 'text-emerald-200'}`}>{fmt(R.geosmin, 1)} ng/L</div>
          <div className="text-xs text-slate-500 mt-1">DWI threshold {DWI_GEOSMIN_NG_L} ng/L {R.breachGeosmin ? '— BREACH' : '— OK'}</div>
        </div>
        <div className={`rounded-xl border-2 p-4 ${R.breachMib ? 'border-red-700 bg-red-900/20' : 'border-emerald-700 bg-emerald-900/15'}`}>
          <div className="text-xs uppercase tracking-wider text-slate-300">Predicted 2-MIB</div>
          <div className={`text-2xl font-bold mt-1 ${R.breachMib ? 'text-red-200' : 'text-emerald-200'}`}>{fmt(R.mib, 1)} ng/L</div>
          <div className="text-xs text-slate-500 mt-1">DWI threshold {DWI_MIB_NG_L} ng/L {R.breachMib ? '— BREACH' : '— OK'}</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Severity (× DWI)</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">× {fmt(R.breachFactor, 2)}</div>
          <div className="text-xs text-slate-500 mt-1">worst of geosmin / MIB</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Treatment cost flag</div>
          <div className="text-lg font-bold text-slate-100 mt-1">{R.breachFactor > 3 ? 'PAC required' : R.breachFactor > 1 ? 'PAC recommended' : 'Standard'}</div>
          <div className="text-xs text-slate-500 mt-1">powdered activated carbon dose</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
        <div>
          <label className="text-slate-400 block mb-1">Chl-a: <strong className="text-slate-100">{chl} µg/L</strong></label>
          <input type="range" min={1} max={100} step={1} value={chl} onChange={e => setChl(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Cyano fraction: <strong className="text-slate-100">{(fCyano * 100).toFixed(0)} %</strong></label>
          <input type="range" min={0} max={1} step={0.02} value={fCyano} onChange={e => setFCyano(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Surface T: <strong className="text-slate-100">{tempC} °C</strong></label>
          <input type="range" min={8} max={30} step={0.5} value={tempC} onChange={e => setTempC(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="flex items-center gap-2 mt-4">
            <input type="checkbox" checked={stratified} onChange={e => setStrat(e.target.checked)} className="w-4 h-4 accent-amber-500" />
            <span className="text-slate-200">Reservoir stratified</span>
          </label>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-rb-brand" /> Why this matters</div>
        <ul className="list-disc ml-5 space-y-1">
          <li>Geosmin and 2-MIB cause customer-complaint earthy / musty taste at concentrations as low as 10 ng/L (≈ 1 drop in an Olympic pool).</li>
          <li>Conventional clarification + chlorination does NOT remove them — Powdered Activated Carbon (PAC) is required, adding ~£0.5-2 per ML cost.</li>
          <li>Cyanobacteria (especially <em>Anabaena</em>, <em>Oscillatoria</em>, <em>Planktothrix</em>) are the dominant producers.</li>
          <li>Stratification raises geosmin x 1.6 by concentrating cyanobacteria in the warm, lit surface layer.</li>
        </ul>
        <p className="mt-2">Aeration disrupts stratification, drops the predicted geosmin by ~40% and typically returns the reservoir
        to "no PAC needed" status, generating direct OPEX savings at the downstream WTW.</p>
        <p className="italic text-slate-500 mt-2">Reference: Wang Z. et al. (2010) Water Research 45(4): 1631-1640, Institute of Hydrobiology, CAS, Wuhan, China.</p>
      </div>
    </div>
  );
}
